import { useState } from 'react';
import { SearchSelect, StatCard, StatCardList } from '@evogard/ui';
import {
  CLAIM_FLOW_DEFAULT_PERIOD,
  CLAIM_FLOW_PERIODS,
  count,
  type ClaimFlowPeriod,
} from '@evogard/domain';
import { ClaimConsultantsChart } from './ClaimConsultantsChart.js';
import { ClaimDeadlinesChart } from './ClaimDeadlinesChart.js';
import { ClaimExitsChart } from './ClaimExitsChart.js';
import { ClaimFlowChart } from './ClaimFlowChart.js';
import { ClaimStatesMap } from './ClaimStatesMap.js';
import { ClaimDurationsChart } from './ClaimDurationsChart.js';
import { ClaimTypesChart } from './ClaimTypesChart.js';
import { ClaimWeeksChart } from './ClaimWeeksChart.js';
import { RepairMoneyCharts } from './RepairMoneyCharts.js';
import {
  useManagementDeadlines,
  useManagementExits,
  useManagementFlow,
} from './useManagement.js';

/**
 * O Painel da Gestão do sinistro (26/10/2026; "Painel do gestor" até 28/10):
 * os gráficos da Gestão do sinistro.
 *
 * O período é um só, no alto, e vale para todos: cada gráfico olha a mesma
 * coorte — os sinistros abertos nos últimos N dias —, e dois gráficos lado a
 * lado com períodos diferentes fariam os números não conversarem.
 *
 * A disposição é a do artefato (aba Gestão › Painel do gestor): os números de
 * relance no alto, o fluxo e as semanas na largura toda e os outros aos pares,
 * numa grade de duas colunas. Cada gráfico mede o próprio cartão (`@container`), e não a
 * tela, para caber na meia coluna.
 */
export function ManagementPanel() {
  const [days, setDays] = useState<ClaimFlowPeriod>(CLAIM_FLOW_DEFAULT_PERIOD);
  return (
    <div className="flex flex-col gap-3.5">
      <div className="w-full sm:w-64">
        <SearchSelect
          label="Período"
          options={CLAIM_FLOW_PERIODS.map((d) => ({
            value: String(d),
            label: `Abertos nos últimos ${d} dias`,
          }))}
          value={String(days)}
          onChange={(v) => v && setDays(Number(v) as ClaimFlowPeriod)}
        />
      </div>
      <Highlights days={days} />
      {/*
       * Largos: o fluxo e as semanas (53 barras no período de um ano). O prazo
       * por etapa lista as vinte etapas e ocupa duas linhas, com o tempo e as
       * saídas empilhados ao lado.
       */}
      <div className="grid gap-3.5 lg:grid-cols-2">
        <div className="lg:col-span-2">
          <ClaimFlowChart days={days} />
        </div>
        <div className="lg:col-span-2">
          <ClaimWeeksChart days={days} />
        </div>
        <div className="grid lg:row-span-2">
          <ClaimDeadlinesChart days={days} />
        </div>
        <ClaimDurationsChart days={days} />
        <ClaimExitsChart days={days} />
        <ClaimStatesMap days={days} />
        <ClaimTypesChart days={days} />
        {/* Largo: a lista de consultores é comprida, e o nome precisa caber. */}
        <div className="lg:col-span-2">
          <ClaimConsultantsChart days={days} />
        </div>
        {/*
         * O dinheiro do Reparo (29/10/2026): o período aqui é o do LANÇAMENTO
         * da despesa e do orçamento, e cada cartão diz isso.
         */}
        <RepairMoneyCharts days={days} />
      </div>
    </div>
  );
}

const share = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

/**
 * Os números de relance. Nenhum é consulta nova: são as mesmas respostas que
 * os gráficos abaixo pedem (a mesma chave de cache), somadas. Enquanto uma
 * delas não chega, o lugar do número é esqueleto — nunca zero.
 */
function Highlights({ days }: { days: ClaimFlowPeriod }) {
  const flow = useManagementFlow(days).data;
  const deadlines = useManagementDeadlines(days).data;
  const exits = useManagementExits(days).data;

  const inProcess = flow?.inProcess.reduce((n, d) => n + d.total, 0);
  const concluded = flow?.links
    .filter((l) => l.to === 'concluido')
    .reduce((n, l) => n + l.total, 0);
  const working = deadlines?.stages.reduce(
    (n, s) => n + s.withinDeadline + s.expired + s.noDeadline,
    0,
  );
  const expired = deadlines?.stages.reduce((n, s) => n + s.expired, 0);
  const left = exits?.exits.reduce((n, e) => n + e.total, 0);

  return (
    <StatCardList stretch>
      <StatCard
        icon="documento"
        density="densa"
        label="Avisos no período"
        value={flow?.total}
        support={flow ? count(flow.thirdParty, 'de terceiro', 'de terceiro') : ''}
      />
      <StatCard
        icon="relogio"
        density="densa"
        label="Em processo"
        value={inProcess}
        support="Análise, Reparo e Indenização"
      />
      <StatCard
        icon="atencao"
        density="densa"
        label="SLA expirado"
        value={expired}
        tone={expired ? 'erro' : 'neutro'}
        support={
          working === undefined || expired === undefined
            ? ''
            : `${share(expired, working)}% dos que estão numa etapa`
        }
      />
      <StatCard
        icon="confirmado"
        density="densa"
        label="Concluídos"
        value={concluded}
        support={
          flow && concluded !== undefined
            ? `${share(concluded, flow.total)}% dos avisos`
            : ''
        }
      />
      <StatCard
        icon="bloqueado"
        density="densa"
        label="Negativas e desistências"
        value={left}
        support={
          exits && left !== undefined ? `${share(left, exits.total)}% dos avisos` : ''
        }
      />
    </StatCardList>
  );
}
